import { Component, computed, inject, input } from '@angular/core';
import { ApiProcess } from '@api/apiProcesses';
import { ProcessRunnerNotificationMessage } from '@api/model/processRunnerNotificationMessage';
import { UserService } from '@core/client/user.service';
import { FileFormat } from '@core/common/files-formats';
import { Module } from '@core/navigation/navigation.model';
import { NavigationService } from '@core/navigation/navigation.service';
import { NotificationsService } from '@core/notifications/notifications.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PROCESSES_NOTIFICATIONS_CONFIG } from '@process-runner/processes.config';
import { LoadingSpinnerComponent } from "@shared/components/loading/loading-spinner/loading-spinner.component";
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';
import { externalUrlsTypeEnum, FileProperties, FileService } from '@shared/services/file.service';
import { VoltaSweetAlertService } from '@shared/services/volta-sweet-alert/volta-sweet-alert.service';
import { firstValueFrom, Observable, of, tap } from 'rxjs';

@Component({
  selector: 'app-process-toast',
  imports: [FontAwesomeModule, TooltipDirective, LoadingSpinnerComponent],
  templateUrl: './process-toast.component.html',
  styleUrl: './process-toast.component.scss',
})
export class ProcessToastComponent {

    private fileService = inject(FileService);
    notificationsService = inject(NotificationsService);
    navigationService = inject(NavigationService);
    private swalService = inject(VoltaSweetAlertService);
    private userService = inject(UserService);

    process = input.required<ProcessRunnerNotificationMessage>();

    processRunnerIcon = computed(() => {
        return this.navigationService.navigation().find((module) => module.name == Module.ProcessRunner)?.icon;
    })
    processStatus = computed(() => this.process().executionDetails.status);
    processConfig = computed(() => {
        let processName = this.process().executionDetails.processName;
        return processName ? PROCESSES_NOTIFICATIONS_CONFIG[processName as ApiProcess] : undefined;
    })
    processMessage = computed(() => {
        let processStatus = this.processStatus();
        return processStatus ? this.processConfig()?.messages[processStatus] : undefined;
    })
    processModule = computed(() => {
        const nameParts = this.process().executionDetails.processName?.split('.');
        const modulesIndex = nameParts?.findIndex((part) => part == 'Modules');
        return modulesIndex ? nameParts?.at(modulesIndex + 1) as Module : undefined;
    })
    moduleConfig = computed(() => {
        return this.navigationService.navigation().find((module) => module.name.replaceAll(' ', '') == this.processModule())
    })
    tenantCompany = computed(() => {
        return this.userService.userCompanies()?.find((company) => company.tenant?.id == this.process().executionDetails.tenantId);
    })
    externalURLCount = computed(() => {
        let count = 0;

        Object.values(externalUrlsTypeEnum).forEach((type) => {
            count += this.process().jobResult?.externalUrls?.[type]?.length ?? 0;
        })

        return count;
    })
    errorCount = computed(() => {
        return this.process().jobResult?.errors?.length ?? 0;
    })
    hasErrorLog = computed(() => {
        return this.processStatus() == 'WithErrors' && this.errorCount() > 0;
    })
    hasExternalLink = computed(() => {
        return this.processStatus() == 'Completed' && this.externalURLCount() > 0;
    })

    blobResults:Record<string,Blob> = {}

    closeProcess(){
        const executionId = this.process().executionDetails.executionId;
        if(executionId){
            this.notificationsService.removeProcessNotification(executionId);
        }
    }

    openErrorLog(){
        const processErrors = this.process().jobResult?.errors;

        if(processErrors){
            // Replaces null values to ''
            const replacer = (key:any, value:any) => (value ?? '');
            const headers = Object.keys(processErrors[0]) as (keyof typeof processErrors[0])[]

            // To remove any column, filter headers here.
            const csvArray = [headers.join(',')] 
            csvArray.push(...processErrors?.map((error) =>
                headers.map((header) => 
                    JSON.stringify(error[header], replacer)
                ).join(',')
            ));
            const csv = csvArray.join('\r\n');

            const blob = new Blob([csv], { type: FileFormat.CSV });
            this.fileService.downloadSingleFile(this.getFileProperties(blob));
            
        }
        else{
            this.swalService.fireError(new Error())
        }
    }

    openExternalURLFile(){

        let filePromises:Array<() => Promise<Blob>> = [];

        Object.values(externalUrlsTypeEnum).forEach((type) => {
            const typeFiles = this.process().jobResult?.externalUrls?.[type];

            typeFiles?.forEach((fileUrl) => {
                switch(type){
                    case externalUrlsTypeEnum.MimeVault:
                        filePromises.push(() => firstValueFrom(this.getBlobObservable(fileUrl)));                        
                }
            })
            
        })

        this.swalService.fireLoading();

        //Get all files
        Promise.allSettled(filePromises.map((fn) => fn()))
        .then((result) => {

            this.swalService.close();

            if(result.length == 1){

                const resultItem = result.at(0);

                if (resultItem?.status === 'fulfilled') {
                    if(resultItem?.value.type == FileFormat.PDF){
                        this.fileService.openSingleFile(this.getFileProperties(resultItem.value));
                    }
                    else{
                        this.fileService.downloadSingleFile(this.getFileProperties(resultItem.value));
                    }
                }
                else{
                    throw new Error(resultItem?.reason);
                }
                
            }
            else if(result.length >= 2) {
                let fileProperties:FileProperties[] = [];

                result.forEach((resultItem) => {
                    if (resultItem?.status === 'fulfilled') {
                        fileProperties.push(this.getFileProperties(resultItem.value));
                    }
                })

                this.fileService.openMultipleFiles(fileProperties);
            }
            else{
                throw new Error();
            }
        })
        .catch((error) => {
            this.swalService.fireError(error);
        })
    }

    getBlobObservable(fileUrl:string):Observable<Blob>{

        if(this.blobResults[fileUrl]){
            return of(this.blobResults[fileUrl]);
        }
        else{
            return this.fileService.getMimeVaultFile(fileUrl, this.tenantCompany()?.id)
            .pipe(
                tap({
                    next:(response) => {
                        this.blobResults[fileUrl] = response;
                    }
                })
            )
        }

    }

    getFileProperties(fileBlob:Blob):FileProperties{
        
        const processName = Object.entries(ApiProcess).find(([key, value]) => value.toString() == this.process().executionDetails.processName)?.[0];

        let name = `${processName}_${this.process().executionDetails.executionId}`;

        if(this.processStatus() == 'WithErrors'){
            name += '_Error_Log'
        }

        return this.fileService.getFileBlobProperties(fileBlob, name);
    }

}
